import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, FormControlLabel,
  CircularProgress, Alert, Stack, Chip, Tooltip, Divider, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  DragHandle as DragIcon, Visibility as VisibleIcon, VisibilityOff as HiddenIcon,
  CloudUpload as UploadIcon, YouTube as YouTubeIcon,
} from '@mui/icons-material';
import iwalkcvService, { IWHeroSlide } from '../../services/iwalkcv.service';

const EMPTY: Omit<IWHeroSlide, 'id'> = {
  ilha: '', titulo: '', subtitulo: '', imagem_url: '', video_url: '', ordem: 0, activo: 1,
};

/** Extrai o ID de um link YouTube (watch, youtu.be, embed ou shorts). Devolve null se não for válido. */
function youtubeId(url?: string): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

export default function CmsHeroSlidesPage() {
  const [slides, setSlides] = useState<IWHeroSlide[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<IWHeroSlide | null>(null);
  const [form, setForm] = useState<Omit<IWHeroSlide, 'id'>>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await iwalkcvService.getHeroSlides();
      setSlides(res.data || []);
    } catch {
      setError('Erro ao carregar slides');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY, ordem: slides.length + 1 });
    setOpen(true);
  }

  function openEdit(slide: IWHeroSlide) {
    setEditing(slide);
    setForm({ ilha: slide.ilha, titulo: slide.titulo, subtitulo: slide.subtitulo || '', imagem_url: slide.imagem_url, video_url: slide.video_url || '', ordem: slide.ordem, activo: slide.activo });
    setOpen(true);
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError('Imagem demasiado grande — máximo 8 MB');
      e.target.value = '';
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await iwalkcvService.uploadHeroImage(file);
      setForm((p) => ({ ...p, imagem_url: url }));
    } catch {
      setError('Erro ao carregar a imagem');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editing) {
        await iwalkcvService.updateHeroSlide(editing.id, form);
      } else {
        await iwalkcvService.createHeroSlide(form);
      }
      setOpen(false);
      load();
    } catch {
      setError('Erro ao guardar slide');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: number) {
    await iwalkcvService.deleteHeroSlide(id);
    setDeleteId(null);
    load();
  }

  async function toggleActivo(slide: IWHeroSlide) {
    await iwalkcvService.updateHeroSlide(slide.id, { ...slide, activo: slide.activo ? 0 : 1 });
    load();
  }

  const f = (label: string, key: keyof typeof form, multiline = false) => (
    <TextField label={label} value={(form as any)[key] ?? ''} fullWidth size="small"
      multiline={multiline} rows={multiline ? 2 : 1}
      onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))} />
  );

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Hero Slides</Typography>
          <Typography variant="body2" color="text.secondary">Gerir as imagens e textos do carrossel da homepage</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
          sx={{ bgcolor: '#8FA800', '&:hover': { bgcolor: '#7a9200' } }}>
          Novo slide
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {['Ordem', 'Prévia', 'Ilha', 'Título', 'Subtítulo', 'Estado', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {slides.map((slide) => (
                <TableRow key={slide.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DragIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                      <Typography variant="body2" fontWeight={600}>{slide.ordem}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box position="relative" width={90} height={52} borderRadius={1} overflow="hidden" bgcolor="action.hover">
                      <img src={slide.imagem_url} alt={slide.ilha} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      {youtubeId(slide.video_url) && (
                        <Tooltip title="Slide com vídeo do YouTube">
                          <YouTubeIcon sx={{
                            position: 'absolute', bottom: 2, right: 2, fontSize: 20,
                            color: '#FF0000', bgcolor: 'white', borderRadius: '4px',
                          }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={slide.ilha} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {slide.titulo}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {slide.subtitulo || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={slide.activo ? 'Visível — clique para ocultar' : 'Oculto — clique para mostrar'}>
                      <IconButton size="small" onClick={() => toggleActivo(slide)}>
                        {slide.activo
                          ? <VisibleIcon fontSize="small" color="success" />
                          : <HiddenIcon fontSize="small" color="disabled" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => openEdit(slide)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => setDeleteId(slide.id)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {slides.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  Nenhum slide. Cria o primeiro clicando em "Novo slide".
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog criar/editar */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar slide' : 'Novo slide'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <Stack direction="row" spacing={2}>
              {f('Ilha (ex: Sal)', 'ilha')}
              <TextField label="Ordem" type="number" value={form.ordem} size="small" sx={{ width: 100, flexShrink: 0 }}
                onChange={(e) => setForm((p) => ({ ...p, ordem: Number(e.target.value) }))} />
            </Stack>
            {f('Título *', 'titulo')}
            {f('Subtítulo', 'subtitulo', true)}

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">Imagem de fundo *</Typography>
            </Divider>

            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              {f('URL da imagem', 'imagem_url')}
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                sx={{ flexShrink: 0, height: 40, whiteSpace: 'nowrap' }}>
                {uploading ? 'A carregar...' : 'Carregar'}
              </Button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                hidden onChange={handleUpload} />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              Cola um link ou carrega um ficheiro do computador (JPG, PNG, WEBP ou GIF — até 8 MB).
            </Typography>

            {form.imagem_url && (
              <Box borderRadius={2} overflow="hidden" height={150} bgcolor="action.hover">
                <img src={form.imagem_url} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            )}

            <Divider textAlign="left">
              <Typography variant="caption" color="text.secondary">Vídeo (opcional)</Typography>
            </Divider>

            <TextField
              label="Link do YouTube"
              value={form.video_url ?? ''}
              fullWidth size="small"
              placeholder="https://www.youtube.com/watch?v=..."
              onChange={(e) => setForm((p) => ({ ...p, video_url: e.target.value }))}
              error={Boolean(form.video_url) && !youtubeId(form.video_url)}
              helperText={
                form.video_url && !youtubeId(form.video_url)
                  ? 'Link do YouTube inválido'
                  : 'Se preencheres, o vídeo é reproduzido no hero e a imagem fica como fundo de arranque.'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><YouTubeIcon fontSize="small" sx={{ color: '#FF0000' }} /></InputAdornment>
                ),
              }}
            />
            {youtubeId(form.video_url) && (
              <Box borderRadius={2} overflow="hidden" height={180} bgcolor="black">
                <iframe
                  title="preview-video"
                  src={`https://www.youtube.com/embed/${youtubeId(form.video_url)}`}
                  style={{ width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            )}
            <FormControlLabel
              control={<Switch checked={Boolean(form.activo)} onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked ? 1 : 0 }))} />}
              label="Visível na homepage" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}
            disabled={saving || uploading || !form.titulo || !form.ilha || !form.imagem_url || (Boolean(form.video_url) && !youtubeId(form.video_url))}
            sx={{ bgcolor: '#8FA800', '&:hover': { bgcolor: '#7a9200' } }}>
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmar eliminação */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Eliminar slide?</DialogTitle>
        <DialogContent>
          <Typography>Esta acção não pode ser desfeita.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => deleteId && handleDelete(deleteId)}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
